import { useMemo } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/es";

dayjs.extend(customParseFormat);
dayjs.locale("es");

/**
 * Periodo de ingreso para NBAPI (0 = 1er cuatrimestre, 1 = 2do) a partir de YYYY-MM-DD,
 * sin pasar por Date (evita corrimientos de zona horaria).
 */
export const getPeriodoFromFecha = (fechaIngreso) => {
  if (!fechaIngreso) return 0;
  const month = parseInt(String(fechaIngreso).split('-')[1], 10);
  if (Number.isNaN(month)) return 0;
  return month >= 1 && month <= 7 ? 0 : 1;
};

const toOption = (value) => {
  const string = dayjs(value, "YYYY-MM-DD").format("MMMM YYYY");
  return { value, label: string.charAt(0).toUpperCase() + string.slice(1) };
};

/**
 * Calendario que usaba el checkout antes de pedir las fechas a NBAPI: los dos proximos periodos
 * (abril/agosto) segun la fecha de hoy, con cierre de inscripcion a agosto el 8 de septiembre.
 * Solo queda como respaldo si el endpoint de fechas falla.
 */
const getFechasRespaldo = (now = new Date()) => {
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const cierreAgosto = new Date(currentYear, 8, 8);
  const nextYear = currentYear + 1;

  if (currentMonth < 5) return [`${currentYear}-04-01`, `${currentYear}-08-01`];
  if (now < cierreAgosto) return [`${currentYear}-08-01`, `${nextYear}-04-01`];
  return [`${nextYear}-04-01`, `${nextYear}-08-01`];
};

/**
 * Fechas de ingreso que ofrece el checkout. Funcion pura: la usan el hook (para el combo del
 * form) y CargarCarrerasIniciales (para pedir las carreras en la fase de carga), asi los dos
 * parten de la MISMA lista y de la MISMA fecha por defecto.
 *
 * - Cursos/eventos y microcredenciales (t = e | m) con ?fi=: la unica fecha es la del link.
 * - Resto: las que devuelve NBAPI para la entidad (fechasIngreso del store, T-2026-158: los
 *   colegios ingresan en marzo). Mientras no hay respuesta, lista vacia; si el pedido fallo, el
 *   calendario anterior.
 */
export const getAvailableDates = (fi, t, fechasIngreso) => {
  if ((t === "e" || t === "m") && fi) {
    return [toOption(fi)];
  }

  if (!fechasIngreso?.cargado) return [];

  const fechas = fechasIngreso.data?.length ? fechasIngreso.data : getFechasRespaldo();
  return fechas.map(toOption);
};

/**
 * Fecha de ingreso con la que arranca el checkout: la guardada (volver atras / recargar) si
 * sigue estando entre las disponibles; si no, ?fi= cuando coincide con una disponible; si no,
 * la primera disponible. Es la fecha con la que se piden las carreras y la que el form muestra
 * seleccionada, para que nunca difieran.
 */
export const getDefaultFechaIngreso = (fi, t, fechaGuardada = null, fechasIngreso) => {
  const dates = getAvailableDates(fi, t, fechasIngreso);
  const esDisponible = (v) => v && dates.some((d) => d.value === v);
  if (esDisponible(fechaGuardada)) return fechaGuardada;
  if (esDisponible(fi)) return fi;
  return dates[0]?.value ?? null;
};

const useAvailableDates = (fi, t) => {
  const fechasIngreso = useSelector((state) => state.carreras.fechasIngreso);
  const dates = useMemo(() => getAvailableDates(fi, t, fechasIngreso), [fi, t, fechasIngreso]);
  const linkConFecha = (t === "e" || t === "m") && fi;

  return { dates, loading: !linkConFecha && !fechasIngreso?.cargado, fechasIngreso };
};

export default useAvailableDates;
